// What has to be true before a billing run can be filed.
//
// Until now a run was a shadow record — an internal check against the manual
// GAMMIS billing, where a missing diagnosis or a lapsed authorisation cost
// nothing because nothing left the building. Filing changes that. Every one of
// these checks exists because the alternative is a rejected claim, an
// underpayment, or a claim filed for care that was already billed once.
//
// The rules are pure functions over rows the caller has already loaded, so
// they can be tested and so the same gate runs in the app and in an edge
// function without being written twice.

import { identityProblems } from './billingIdentity.js';
import { resolvePriorAuth, AUTH_REASON_LABEL } from '../priorAuth.js';
import { coverageProblems } from '../eligibility.js';

/** Runs on the old footing are history, not a filing queue. */
export const FIRST_FILEABLE_WEEK_START = '2026-08-01';

/** What the manual GAMMIS billing charges over the Medicaid-payable amount. */
export const EXPECTED_CHARGE_MULTIPLIER = 2.5;

const day = (v) => (typeof v === 'string' && v.length >= 10 ? v.slice(0, 10) : null);
const dow = (iso) => new Date(`${iso}T12:00:00Z`).getUTCDay();

const describe = (line) =>
  `${line.client_name || line.medicaid_id || 'unknown client'} on ${day(line.service_date) || 'an unknown date'}`;

/**
 * Every reason this run cannot be filed, as human sentences.
 *
 * Returns `{ blocking, warnings, identityIssues, runIssues, ok }`. Blocking is
 * everything that stops submission; `identityIssues` and `runIssues` split it
 * by cause, because an incomplete billing identity is one setting to fix for
 * every run while a missing diagnosis belongs to one line of one run, and a
 * screen that shows both in the same list makes the second look as
 * administrative as the first.
 *
 * Nothing here is auto-corrected — a run that cannot be filed correctly is a
 * thing for a person to look at, not for code to guess at.
 */
export const preflightRun = ({
  run,
  lines = [],
  clients = [],
  authHistory = {},
  identity,
  serviceCatalog = [],
  eligibility = null,
  eligibilityAutomationOn = false,
} = {}) => {
  const identityIssues = identityProblems(identity, { forTransmission: true });
  const blocking = [];
  const warnings = [];

  // ── The run's footing ─────────────────────────────────────────────────────
  if (!run) {
    blocking.push('No billing run supplied.');
    return done();
  }

  // eslint-disable-next-line no-inner-declarations
  function done() {
    return {
      identityIssues,
      runIssues: blocking,
      blocking: [...identityIssues, ...blocking],
      warnings,
      ok: identityIssues.length === 0 && blocking.length === 0,
    };
  }

  const from = day(run.date_from);
  const to   = day(run.date_to);

  if (!from || !to) {
    blocking.push('The run has no date range.');
  } else {
    if (from < FIRST_FILEABLE_WEEK_START) {
      blocking.push(
        `This run covers ${from} to ${to}, before ${FIRST_FILEABLE_WEEK_START}. ` +
        'Runs from the old footing are a historical record — most of their lines already ' +
        'carry a real GAMMIS remittance — and are never filed.',
      );
    }
    // Sunday start, Saturday end. Payroll has always worked this way; billing
    // moved to match when the run became a filing, so the week ending Friday
    // is the shape of a shadow run, not a fileable one.
    if (dow(from) !== 0 || dow(to) !== 6) {
      warnings.push(`This run covers ${from} to ${to}, which is not a Sunday–Saturday week.`);
    }
  }

  if (Number(run.charge_multiplier) !== EXPECTED_CHARGE_MULTIPLIER) {
    blocking.push(
      `This run charges ${run.charge_multiplier}x the payable amount; filed runs charge ` +
      `${EXPECTED_CHARGE_MULTIPLIER}x, which is what the manual GAMMIS billing charges.`,
    );
  }
  if (!run.hours_basis) {
    blocking.push('This run does not record which hours basis it was built on.');
  }
  if (run.status !== 'approved') {
    blocking.push(`This run is ${run.status || 'in an unknown state'}; only an approved run can be filed.`);
  }

  // ── The AxisCare/CAERA merge seam ─────────────────────────────────────────
  // Identical rule to open-scheduled-runs: `billable` decides, not the presence
  // of a procedure code. SUPERVISION is CAERA-native, carries the sentinel code
  // NONBILL at rate 0, and reading it as billable refuses every run outright.
  const switched = (serviceCatalog || []).filter((c) =>
    c.active && c.billable !== false && c.visit_source === 'caera' &&
    c.procedure_code && c.procedure_code !== 'NONBILL');
  if (switched.length) {
    blocking.push(
      `Service line(s) ${switched.map((c) => c.code).join(', ')} now bill from CAERA rather than ` +
      'AxisCare. Filing before the merge seam is applied would bill the same care twice.',
    );
  }

  // ── The lines ─────────────────────────────────────────────────────────────
  if (!lines.length) {
    blocking.push('This run has no claim lines.');
    return done();
  }

  const clientById = new Map((clients || []).map((c) => [c.id, c]));
  const missingDx = [];
  const missingAuth = [];
  const lapsedAuth = [];
  const missingMedicaidId = [];
  const missingDemographics = [];
  const zeroUnits = [];

  for (const line of lines) {
    const client = clientById.get(line.client_id) || null;

    if (!String(line.diagnosis_code || '').trim()) missingDx.push(describe(line));

    // allowLapsed mirrors what the export will actually do, so the warning
    // describes the file that would be produced rather than a hypothetical one.
    const auth = resolvePriorAuth({
      history: authHistory[line.client_id] || [],
      client,
      date: day(line.service_date),
      allowLapsed: true,
    });
    if (!auth.number) {
      missingAuth.push(`${describe(line)} — ${AUTH_REASON_LABEL[auth.reason] || auth.reason}`);
    } else if (auth.source === 'lapsed') {
      lapsedAuth.push(`${describe(line)} — ${auth.number}, expired ${auth.expiresOn}`);
    }

    if (!String(client?.medicaid_id || line.medicaid_id || '').trim()) {
      missingMedicaidId.push(describe(line));
    }
    if (client && (!client.date_of_birth || !client.gender)) {
      missingDemographics.push(describe(line));
    }
    if (!(Number(line.units) > 0)) zeroUnits.push(describe(line));
  }

  const listing = (items, limit = 5) =>
    items.slice(0, limit).join('; ') + (items.length > limit ? ` and ${items.length - limit} more` : '');

  if (missingDx.length) {
    blocking.push(
      `${missingDx.length} line(s) have no diagnosis code: ${listing(missingDx)}. ` +
      'A claim needs one and there is nothing safe to substitute.',
    );
  }
  // A WARNING, NOT A BLOCK, and that is a deliberate reversal (2026-09-26).
  //
  // An authorisation problem is denied by GAMMIS at CLAIM level — Georgia
  // validates fee-for-service per claim, so one bad line denies itself and the
  // rest of the file adjudicates normally. Blocking the run meant one client's
  // lapsed paperwork held back everyone else's revenue, and worse, held back
  // their own: a denied claim still establishes the filing date, so waiting
  // risks the timely-filing window on the very lines it was trying to protect.
  //
  // A missing DIAGNOSIS stays blocking. That is a different class of fault —
  // loop 2300 requires HI, so a claim without one is a structural violation
  // and the whole interchange is rejected, everyone's claims with it.
  if (missingAuth.length) {
    warnings.push(
      `${missingAuth.length} line(s) have no prior authorisation at all: ${listing(missingAuth)}. ` +
      'They will be filed without one and are expected to deny CO-15.',
    );
  }
  if (lapsedAuth.length) {
    warnings.push(
      `${lapsedAuth.length} line(s) will be filed with a LAPSED authorisation: ${listing(lapsedAuth)}. ` +
      'Expect a denial for dates outside the authorised period — resubmit once the renewal is issued, ' +
      'or it may reprocess on its own if the new authorisation is dated to cover the gap.',
    );
  }
  if (missingMedicaidId.length) {
    blocking.push(`${missingMedicaidId.length} line(s) have no Medicaid ID: ${listing(missingMedicaidId)}.`);
  }
  if (missingDemographics.length) {
    blocking.push(
      `${missingDemographics.length} line(s) belong to a client with no date of birth or gender on file: ` +
      `${listing(missingDemographics)}. Both are required in the subscriber loop.`,
    );
  }
  if (zeroUnits.length) {
    blocking.push(`${zeroUnits.length} line(s) have no units: ${listing(zeroUnits)}.`);
  }

  // ── Coverage ──────────────────────────────────────────────────────────────
  // A WARNING, never a block (decided 2026-10-24). GAMMIS denies an uncovered
  // member's claim by itself, at claim level, like an authorisation problem —
  // and the person filing may know better than the last check did. `eligibility`
  // is each client's newest eligibility_checks row; omitted, nothing is said.
  // "Not confirmed for this month" is asked only once the monthly check is
  // switched on: before that it would be true of every child, every week.
  if (eligibility) {
    const seen = new Set();
    const coverage = [];
    for (const line of lines) {
      if (!line.client_id || seen.has(line.client_id)) continue;
      seen.add(line.client_id);
      const client = clientById.get(line.client_id) || null;
      const problems = coverageProblems({
        payer: client?.service_type, latest: eligibility[line.client_id] || null,
        asOf: day(run.date_from), includeUnconfirmed: eligibilityAutomationOn,
      });
      if (problems.length) {
        coverage.push(`${line.client_name || client?.last_name || 'a client'} — ${problems.map(p => p.text).join('; ')}`);
      }
    }
    if (coverage.length) {
      warnings.push(
        `Coverage for ${coverage.length} client(s) needs a look before filing: ${listing(coverage)}. ` +
        'Their lines can still be filed; an uncovered member\'s claims are expected to deny.',
      );
    }
  }

  return done();
};
